import axios from 'axios';
import { throttle, DebouncedFunc } from 'lodash';
import { Rollout, RolloutMultiFetch } from '../interfaces';
import { config } from '../configuration';
import { time } from '../metrics';
import { limitExecution, baseUrl } from './utils';

/**
 * The cache to store throttled fetch functions.
 * Key = comma separated rollout names as a string.
 * Value = throttled fetch function for those rollouts.
 *
 * @type {Map<string, DebouncedFunc<RolloutMultiFetch>>}
 */
const cache = new Map<string, DebouncedFunc<RolloutMultiFetch>>();

/**
 * Fetches multiple rollouts from the server using the refresh timeout.
 *
 * @param {string[]} names The rollouts to fetch.
 * @returns {Promise<Rollout[]>}
 */
export const fetchMultiRolloutData = async (names: string[]): Promise<Rollout[]> => {
    const start = process.hrtime.bigint();
    const { data }: { data: Rollout[] } = await limitExecution(
        axios.post(`${baseUrl(config)}/features-core-multi`, { rollouts: names }),
        config.refreshTimeout
    );

    time(`api.refresh_multi.${names.length}`, start);

    return data;
};

/**
 * Similar to 'fetchMultiRolloutData' function but using lodash's 'throttle' to limit executions to once per throttle window.
 *
 * @param {string[]} rollouts The rollout to fetch.
 * @returns {Promise<Rollout[]>}
 */
export const throttledFetchMultiRolloutData = (rollouts: string[]): Promise<Rollout[]> => {
    let throttledFetch: DebouncedFunc<RolloutMultiFetch>;

    const key = rollouts.join(',');

    if (cache.has(key)) {
        throttledFetch = cache.get(key) as DebouncedFunc<RolloutMultiFetch>;
    } else {
        throttledFetch = throttle(fetchMultiRolloutData, config.throttleWindow, { leading: true, trailing: false });
        cache.set(key, throttledFetch);
    }

    return throttledFetch(rollouts) as Promise<Rollout[]>;
};
